import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  inject,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PointsSummaryFacade } from '@features/points-summary/facades/points-summary-facade/points-summary.facade';
import {
  CheckoutLinkedAccountPosition,
  TransferConnectV2SettingService,
} from '@features/transfer-connect-v2/services/transfer-connect-v2-setting/transfer-connect-v2-setting.service';
import { SubmitCheckoutFormData } from '@features/transfer-connect-v2/store/actions';
import { TransferConnectV2State } from '@features/transfer-connect-v2/store/reducers';
import { TransferConnectV2CheckoutFormData } from '@features/transfer-connect-v2/types';
import { TcV2CheckoutComponent } from '@features/transfer-connect-v2/views/checkout/checkout.component';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { getAuthIsLoggedIn } from '@rd/core/auth/store/selectors/auth.selectors';
import { LocaleFacade } from '@rd/core/facades/locale-facade/locale.facade';
import { AnalyticsService } from '@rd/core/services/analytics/analytics.service';
import { PageTitleService } from '@rd/core/services/page-title/page-title.service';
import { CoreState } from '@rd/core/store/reducers';
import { getTenantBootstrapTimeZoneOffset } from '@rd/core/tenant/store/selectors/tenant.selectors';
import { CORE_AUTH_SECURE_ROUTE_DIALOG } from '@rd/core/tokens/popup.token';
import { LoyaltyProgramHelper } from '@rd/helpers/loyalty-program.helper';
import { DialogService } from '@rd/popup/services/dialog/dialog.service';
import { PopupSettingService } from '@rd/popup/services/popup-setting/popup-setting.service';
import { CheckoutErrorDialogComponent } from '@rd/shared-legacy/components/checkout-error-dialog/checkout-error-dialog.component';
import { Observable } from 'rxjs';
import { map, take, withLatestFrom } from 'rxjs/operators';

@Component({
  selector: 'rd-anz-tcv2-checkout',
  templateUrl: './checkout.component.html',
  styleUrls: ['./checkout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AnzTcV2CheckoutComponent
  extends TcV2CheckoutComponent
  implements OnInit
{
  @HostBinding('class.rd-anz-tcv2-checkout') componentClass = true;

  linkedAccountPosition: CheckoutLinkedAccountPosition;
  timeZoneOffset: string | undefined;

  authIsLoggedIn$: Observable<boolean> = this.store
    .select(getAuthIsLoggedIn)
    .pipe(map((a) => !!a));

  private coreAuthSecureRouteDialogComponentType = inject(
    CORE_AUTH_SECURE_ROUTE_DIALOG,
  );

  constructor(
    protected store: Store<{
      core: CoreState;
      transferConnectV2: TransferConnectV2State;
    }>,
    protected dialogService: DialogService,
    protected loyaltyProgramHelper: LoyaltyProgramHelper,
    protected route: ActivatedRoute,
    protected router: Router,
    protected translateService: TranslateService,
    protected analyticsService: AnalyticsService,
    protected popupSettingService: PopupSettingService,
    protected pointsSummaryFacade: PointsSummaryFacade,
    protected pageTitleService: PageTitleService,
    protected localeFacade: LocaleFacade,
    protected transferConnectV2SettingService: TransferConnectV2SettingService,
  ) {
    super(
      store,
      dialogService,
      loyaltyProgramHelper,
      route,
      router,
      translateService,
      analyticsService,
      popupSettingService,
      pointsSummaryFacade,
      pageTitleService,
      localeFacade,
      transferConnectV2SettingService,
    );
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.linkedAccountPosition =
      this.transferConnectV2SettingService.linkedAccountPosition;
    this.store
      .select(getTenantBootstrapTimeZoneOffset)
      .subscribe((timeZoneOffset: string | undefined) => {
        this.timeZoneOffset = timeZoneOffset;
      });
  }

  get shouldRenderEnrollmentText(): boolean {
    return (
      !this.loyaltyProgramMembership &&
      !!this.loyaltyProgram.pointsTransferCapability.enrollmentUrl
    );
  }

  get shouldRenderLinkedAccountOnTop(): boolean {
    return this.linkedAccountPosition === 'top';
  }

  get promotionTranslationKey(): string | undefined {
    const startTime = new Date(`2021-10-26T00:00${this.timeZoneOffset}`);
    const endTime = new Date(`2021-11-22T00:00${this.timeZoneOffset}`);
    const currentTime = new Date();

    if (
      this.loyaltyProgram.transferConnectIdentifier === 'SQKF' &&
      currentTime >= startTime &&
      currentTime < endTime
    ) {
      return 'wl.anz.transfer_v2.promotion.SQKF';
    }
  }

  submitCheckoutFormData(
    formData: Partial<TransferConnectV2CheckoutFormData>,
  ): void {
    // check if formData.transfer amount > store.
    this.pointsSummaryFacade.pointsSummary$
      .pipe(take(1), withLatestFrom(this.authIsLoggedIn$))
      // @ts-ignore
      .subscribe(([{ pointsBalance }, isLoggedIn]) => {
        if (!isLoggedIn) {
          this.dialogService.open(this.coreAuthSecureRouteDialogComponentType, {
            id: 'auth-secure-route-dialog',
            screenMode: 'center-md',
            ariaLabel: this.translateService.instant(
              'accessibility.core.auth_dialog_aria_label',
            ),
            keepModalOnRouteChange: true,
          });
          return;
        }
        // @ts-ignore
        if (formData.sourceAmount > pointsBalance) {
          const loyaltyCurrency =
            this.loyaltyCurrencySelected[
              this.transferConnectV2SettingService
                .insufficientMilesCurrencyNameAttribute
            ];

          return this.dialogService.open(CheckoutErrorDialogComponent, {
            data: {
              ctaButtonText: this.translateService.instant('modal.error.ok'),
              errorMessageDescription: this.translateService.instant(
                'fulfillment.insufficient_miles',
                { currencyShortName: loyaltyCurrency },
              ),
              errorMessageTitle: this.translateService.instant(
                'fulfillment.insufficient_miles_title',
                { loyaltyCurrency },
              ),
              iconName: this.popupSettingService.insufficientMilesIconType,
            },
            id: 'rd-shared-checkout-error-dialog',
            screenMode: 'center-md',
            ariaLabel: this.translateService.instant(
              'fulfillment.insufficient_miles_title',
              { loyaltyCurrency },
            ),
          });
        }

        this.store.dispatch(
          new SubmitCheckoutFormData({
            loyaltyProgramId: this.loyaltyProgram.id,
            formData: {
              ...formData,
              membershipId: '',
            } as TransferConnectV2CheckoutFormData,
          }),
        );

        this.router.navigate(
          [`/transfer/checkout/${this.loyaltyProgram.id}/membership`],
          {
            queryParams: { loyaltyProgramId: this.loyaltyProgram.id },
          },
        );
      });
  }
}
